import type { ReactNode } from 'react';
import { OutboundLink } from './LeavingDialog';
import { TechArt } from './TechArt';
import { LiveNetwork } from './LiveNetwork';
import { COMPANY_SITE, COMPANY_SITE_LABEL } from '../lib/site';

/**
 * The signed-out shell: the brand on one side, the form on the other.
 *
 * ## Why it takes a side rather than each page drawing its own
 *
 * Sign-in had this layout and the forgotten-password page did not — it was a
 * card floating in the middle of an empty background, which made following the
 * link feel like leaving the application rather than moving within it.
 *
 * ## Why the sides swap
 *
 * `reversed` puts the form on the left and the panel on the right, and it is
 * `flex-row-reverse` that does it — the DOM order never changes. That matters
 * for two reasons beyond the look of it: a screen reader and the tab key follow
 * the source, so the form is still reached first either way; and the panel is
 * `hidden lg:flex`, so on a phone there is only ever one column and the whole
 * question disappears.
 *
 * The movement is the point. Going from sign-in to "forgotten your password"
 * swings the panel across the screen, which says *you are somewhere else now*
 * more clearly than a heading does — and coming back swings it home.
 */
export function SplitScreen({
  reversed = false,
  children,
}: {
  /** Form on the left, brand on the right. */
  reversed?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={`flex min-h-screen ${reversed ? 'lg:flex-row-reverse' : ''}`}
      /*
       * Only the panel slides, and only for somebody who has not asked for less
       * motion — the reduced-motion rule lives in index.css alongside the rest.
       */
      data-side={reversed ? 'right' : 'left'}
    >
      {/*
        `relative` and `overflow-hidden` are both load-bearing: the first gives
        the canvas something to fill, the second stops a pulse that reaches the
        edge of the panel from widening the page.
      */}
      <div className="relative hidden flex-1 flex-col justify-between overflow-hidden gradient-hero p-12 text-white lg:flex">
        {/* Behind everything, and it never takes a click. */}
        <LiveNetwork />

        {/*
          Each block below is lifted above the canvas with `relative`. Without
          it they would share the stacking context with the canvas and the
          painting order alone would decide what covers what — which happens to
          work today and would quietly stop working the first time somebody
          reorders this file.
        */}
        <OutboundLink
          href={COMPANY_SITE}
          className="relative self-start"
          title={`GoSpeedNet — ${COMPANY_SITE_LABEL}`}
        >
          <img
            src="/brand/logo.png"
            alt={`GoSpeedNet — visit ${COMPANY_SITE_LABEL}`}
            className="h-10 w-auto rounded bg-white p-2"
          />
        </OutboundLink>

        {/* Drawn, not fetched: transparent by construction, allowed by the
            content-security-policy, and nobody's copyright. */}
        <TechArt className="relative text-white" />

        <div className="relative">
          <h1 className="font-heading text-4xl leading-tight">Field Dispatch</h1>
          <p className="mt-3 max-w-md text-white/80">
            Everything between a customer asking for internet and a technician finishing the job —
            leads and contracts, the ticket board and the people on it, stock, invoices and
            payments by M-Pesa. Built for GoSpeedNet in Kisii.
          </p>
        </div>

        <OutboundLink
          href={COMPANY_SITE}
          className="relative text-sm text-white/60 hover:text-white/90"
        >
          Seamless Connectivity · {COMPANY_SITE_LABEL}
        </OutboundLink>
      </div>

      <div className="flex flex-1 items-center justify-center p-6">{children}</div>
    </div>
  );
}
